/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { reaction } from 'mobx';
import * as THREE from 'three';
import type { IImageSliceViewModel } from '@/engine/app/interfaces';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { IPresentationChangeHandler } from './Interfaces';

export interface IImagePresentationPlaneStrategy extends IDisposable {
  onPlaneCreated: (
    planeMesh: THREE.Mesh,
    imageModel: IImageSliceViewModel,
    changeHandler: IPresentationChangeHandler
  ) => void;
  onPlaneChanged: (edgesGeometry: THREE.BufferGeometry) => void;
}

export class Image2dPresentationPlaneStrategy implements IImagePresentationPlaneStrategy {
  public dispose(): void {
    // noop
  }

  public onPlaneCreated(planeMesh: THREE.Mesh): void {
    planeMesh.visible = true;
  }

  public onPlaneChanged(): void {
    // noop
  }
}

export class Image3dPresentationPlaneStrategy implements IImagePresentationPlaneStrategy {
  private _reactionDisposer?: IDisposable;

  private _planeMesh?: THREE.Mesh;

  private _edgesGeometry?: THREE.BufferGeometry;

  private _edgesMaterial?: THREE.LineBasicMaterial;

  private _planeEdges?: THREE.LineSegments;

  public onPlaneCreated(
    planeMesh: THREE.Mesh,
    imageModel: IImageSliceViewModel,
    changeHandler: IPresentationChangeHandler
  ): void {
    this.dispose();

    this._edgesGeometry =
      planeMesh.geometry.hasAttribute('position') && planeMesh.geometry.attributes.position.count
        ? new THREE.EdgesGeometry(planeMesh.geometry)
        : new THREE.BufferGeometry();
    this._edgesMaterial = new THREE.LineBasicMaterial({
      color: new THREE.Color(imageModel.color)
    });
    this._planeEdges = new THREE.LineSegments(this._edgesGeometry, this._edgesMaterial);
    this._planeMesh = planeMesh;
    this._planeMesh.add(this._planeEdges);

    this._reactionDisposer = new AutoDisposer(
      reaction(
        () => imageModel.isVisible,
        (visibility) => {
          planeMesh.visible = visibility;
          changeHandler.handlePresentationChange();
        }
      )
    );
  }

  public onPlaneChanged(edgesGeometry: THREE.BufferGeometry): void {
    if (this._edgesGeometry) {
      this._edgesGeometry.dispose();
    }
    this._edgesGeometry = new THREE.EdgesGeometry(edgesGeometry);
    this._planeEdges!.geometry = this._edgesGeometry;
    this._edgesMaterial!.needsUpdate = true;
  }

  public dispose(): void {
    if (this._reactionDisposer) {
      this._reactionDisposer.dispose();
      this._reactionDisposer = undefined;
    }
    if (this._planeEdges) {
      this._planeMesh?.remove(this._planeEdges);
      this._planeEdges = undefined;
    }
    if (this._edgesGeometry) {
      this._edgesGeometry.dispose();
      this._edgesGeometry = undefined;
    }
    if (this._edgesMaterial) {
      this._edgesMaterial.dispose();
      this._edgesMaterial = undefined;
    }
    this._planeMesh = undefined;
  }
}
